import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

function parseBudgetToAmount(budget) {
  const s = String(budget || '').trim();
  if (!s) return null;
  if (s.startsWith('+')) {
    const n = Number(s.replace(/\D/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const rangeMatch = s.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    const high = Number(rangeMatch[2]);
    return Number.isFinite(high) && high > 0 ? high : null;
  }
  const n = Number(s.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return '0,00 €';
  return `${n.toFixed(2).replace('.', ',')} €`;
}

function RequestDemandStep12() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();
  const [showPayDialog, setShowPayDialog] = useState(false);

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestStep9Details = location.state?.requestStep9Details || {};
  const requestStep11Details = location.state?.requestStep11Details || {};
  const userEmail = requestStep11Details.email || '';
  const emailSent = Boolean(location.state?.emailSent);
  const emailError = location.state?.emailError || '';
  const isStep5Skipped = location.state?.isStep5Skipped || false;
  const displayStep = isStep5Skipped ? 11 : 12;
  const requestId = location.state?.requestId || '';
  const paymentAmount =
    parseBudgetToAmount(requestStep9Details.customBudget || requestStep9Details.budget) || 149;

  const emailMessage = emailSent
    ? t('wizard.step12EmailSent', { email: userEmail || t('wizard.step12EmailFallback') })
    : t('wizard.step12EmailFailed', { error: emailError ? ` ${emailError}` : '' });

  return (
    <AppLayout title={`${t('wizard.step', { n: displayStep })} - ${t('wizard.step12Title')}`} rootClass="layout-1">
      <style>{`
        .request-step12-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step12-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 3.8rem;
          padding-bottom: 2rem;
        }
        .request-step12-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.2rem;
        }
        .request-step12-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        .request-step12-description {
          color: #5f6b7a;
          margin-bottom: 1.6rem;
          max-width: 620px;
        }
        .request-step12-box {
          border: 1px solid #d6dde5;
          border-radius: 10px;
          background: #fff;
          padding: 1.3rem 1rem;
          text-align: center;
          margin-bottom: 1.6rem;
        }
        .request-step12-box-title {
          color: #0f2b3d;
          font-size: 1.9rem;
          font-weight: 700;
          margin-bottom: 0.9rem;
        }
        .request-step12-check {
          width: 160px;
          height: 160px;
          margin: 0 auto 0.95rem;
          display: block;
        }
        .request-step12-msg {
          color: #0f2b3d;
          font-weight: 500;
          max-width: 560px;
          margin: 0 auto 0.95rem;
        }
        .request-step12-info {
          border: 1px solid #60a5fa;
          border-radius: 8px;
          background: #eaf4ff;
          color: #1d4ed8;
          padding: 0.7rem;
          text-align: left;
          max-width: 560px;
          margin: 0 auto;
        }
        .request-step12-list-title {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.9rem;
        }
        .request-step12-list {
          display: grid;
          gap: 0.8rem;
        }
        .request-step12-item {
          display: flex;
          gap: 0.55rem;
          color: #0f2b3d;
          align-items: flex-start;
        }
        .request-step12-num {
          width: 26px;
          height: 26px;
          border-radius: 999px;
          background: #2563eb;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          flex-shrink: 0;
          margin-top: 0.05rem;
        }
        .request-step12-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
        .step12-pay-banner {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          background: #eff6ff;
          color: #1e40af;
          padding: 0.65rem 0.85rem;
          font-size: 0.88rem;
          margin: 0 0 1.5rem;
        }
        .step12-pay-banner svg {
          flex-shrink: 0;
          margin-top: 0.1rem;
        }
        .step12-dialog-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.5);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        .step12-dialog {
          background: #fff;
          border-radius: 14px;
          padding: 1.75rem 1.5rem 1.5rem;
          max-width: 440px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
        }
        .step12-dialog-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 0.35rem;
        }
        .step12-dialog-amount {
          font-size: 1.7rem;
          font-weight: 700;
          color: #1d4ed8;
          margin: 0 0 1rem;
        }
        .step12-dialog-reminders {
          list-style: none;
          padding: 0;
          margin: 0 0 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .step12-dialog-reminders li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #334155;
        }
        .step12-dialog-reminders li svg {
          flex-shrink: 0;
          margin-top: 0.15rem;
          color: #16a34a;
        }
        .step12-dialog-actions {
          display: flex;
          gap: 0.6rem;
          justify-content: flex-end;
        }
        .step12-dialog-cancel {
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #475569;
          border-radius: 8px;
          padding: 0.55rem 1rem;
          font-size: 0.92rem;
          cursor: pointer;
        }
        .step12-dialog-cancel:hover {
          background: #f1f5f9;
        }
        .step12-dialog-confirm {
          border: none;
          background: #16a34a;
          color: #fff;
          border-radius: 8px;
          padding: 0.55rem 1.1rem;
          font-size: 0.92rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .step12-dialog-confirm:hover {
          background: #15803d;
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step12-page p-3 p-md-4">
            <div className="request-step12-content">
              <div className="request-step12-step">{t('wizard.step', { n: displayStep })}</div>
              <h2 className="request-step12-heading">{t('wizard.step12Heading')}</h2>
              <p className="request-step12-description">{t('wizard.step12Description')}</p>

              <div className="request-step12-box">
                <div className="request-step12-box-title">{t('wizard.step12Confirmed')}</div>
                <svg className="request-step12-check" viewBox="0 0 160 160" aria-hidden="true">
                  <circle cx="80" cy="80" r="64" fill="none" stroke="#17b317" strokeWidth="5" />
                  <path
                    d="M53 82 L69 97 L104 62"
                    fill="none"
                    stroke="#17b317"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="request-step12-msg">{emailMessage}</div>
                <div className="request-step12-info">{t('wizard.step12SpamNote')}</div>
              </div>

              <h3 className="request-step12-list-title">{t('wizard.step12NextSteps')}</h3>
              <div className="request-step12-list">
                <div className="request-step12-item"><span className="request-step12-num">1</span><span>{t('wizard.step12Next1')}</span></div>
                <div className="request-step12-item"><span className="request-step12-num">2</span><span>{t('wizard.step12Next2')}</span></div>
                <div className="request-step12-item"><span className="request-step12-num">3</span><span>{t('wizard.step12Next3')}</span></div>
                <div className="request-step12-item"><span className="request-step12-num">4</span><span>{t('wizard.step12Next4')}</span></div>
              </div>
            </div>

            <div className="step12-pay-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>{t('wizard.payInfoBanner')}</span>
            </div>

            <div className="request-step12-footer">
              <NioButton className="btn btn-primary" label={t('common.back')} onClick={() => navigate(-1)} />
              <NioButton
                className="btn btn-success"
                label={t('wizard.payNow')}
                onClick={() => setShowPayDialog(true)}
              />
              <NioButton
                className="btn btn-primary"
                label={t('wizard.step12Finish')}
                onClick={() => navigate('/index-bs-management')}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>

      {showPayDialog && (
        <div className="step12-dialog-overlay" onClick={() => setShowPayDialog(false)}>
          <div className="step12-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="step12-dialog-title">{t('wizard.payNowDialogTitle')}</p>
            <div className="step12-dialog-amount">
              <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '0.1rem' }}>
                {t('wizard.payNowDialogAmount')}
              </span>
              {formatMoney(paymentAmount)}
            </div>
            <ul className="step12-dialog-reminders">
              {[t('wizard.payNowReminder1'), t('wizard.payNowReminder2'), t('wizard.payNowReminder3')].map((r, i) => (
                <li key={i}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <div className="step12-dialog-actions">
              <button className="step12-dialog-cancel" onClick={() => setShowPayDialog(false)}>
                {t('wizard.payNowCancel')}
              </button>
              <button
                className="step12-dialog-confirm"
                onClick={() => {
                  setShowPayDialog(false);
                  navigate('/payment', {
                    state: {
                      amount: paymentAmount,
                      requestId: requestId || undefined,
                      description: t('wizard.step12PaymentDescription', {
                        parent: translateServiceLabel(t, parentServiceTitle),
                        subService: translateServiceLabel(t, selectedSubService),
                        title: projectTitle ? ` (${projectTitle})` : '',
                      }),
                    },
                  });
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                {t('wizard.payNowConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default RequestDemandStep12;
